import { Alert, Box, Stack, Tab, Tabs, capitalize, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { Spinner } from "UI/Spinner";
import { DialogWindow } from "./DialogWindow";
import * as SC from "./ReferralDetails.style";
import { Messages } from "./Messages";
import Status from "./Status";
import { useTabs } from "hooks/useTabs";
import { TabPanel } from "UI/TabPanel";
import { FileList } from "./FileList";
import { displayDobTime, displayLocalDateTime } from "utils";

// const convertDate = (value) => {
//   const date = value ? new Date(value) : undefined;
//   return date
//     ? new Intl.DateTimeFormat("en-US", {
//         dateStyle: "short",
//         timeStyle: "short",
//       }).format(date)
//     : undefined;
// };

export const ReferralDetails = ({ originator, id, handleClose, showError }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [schedulers, setSchedulers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);

  const { currentTab, handleChangeTab } = useTabs({
    key: "ReferralDetails",
    userData: originator,
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/referral_history`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id,
            accessToken: originator?.dashboardAccessToken ?? "",
            action: "GET_DETAILS",
          }),
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        setError(
          capitalize(result?.message ?? result?.error ?? "Something went wrong")
        );
        return;
      }
      setData(result?.referral);
      setAdmins(result?.admins);
      setMarketers(result?.marketers);
      setSchedulers(result?.schedulers);
      setProviders(result?.providers);
      setPatient(result?.patient);
      setFiles(result?.files);
    } catch (error) {
      setError(
        capitalize(error?.message ?? error?.error ?? "Something went wrong")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // if (loading) return <Spinner />;

  const {
    type,
    status,
    recipientName,
    recipientPhone,
    recipientAddress,
    createdAt,
    buttonId,
    question,
  } = data ?? {};

  const isQuestion = type === "Question";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <DialogWindow
      title={`${type ?? "Referral"} Details`}
      content={
        <Stack width={isMobile ? "100%" : "80vw"} maxWidth={"1196px"} minHeight={"450px"}>
          <Box
            sx={{
              marginBottom: 2,
              flex: 0,
              borderBottom: 1,
              borderColor: "divider",
              width: "100%",
            }}
          >
            <Tabs
              value={currentTab}
              onChange={handleChangeTab}
              aria-label="tabs selector"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Details" />
              <Tab
                label={`Attachments ${
                  files?.length > 0 ? `(${files?.length})` : ""
                }`}
              />
            </Tabs>
          </Box>
          <TabPanel value={currentTab} index={0}>
            <Stack
              direction={"row"}
              maxWidth={"1200px"}
              flexWrap={"wrap"}
              gap={4}
            >
              <Stack flex={1} minWidth={"240px"} gap={2} mt={2}>
                <Stack width={"100%"} gap={1}>
                  <SC.InfoSection>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <SC.Label>Type:</SC.Label>
                      {type}
                    </Stack>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <SC.Label>Status:</SC.Label>
                      <Status value={status} sx={{ padding: "8px 16px" }} />
                    </Stack>
                    <SC.Info>
                      <SC.Label>Created at: </SC.Label>
                      {displayLocalDateTime(createdAt)}
                    </SC.Info>
                    <SC.Info>
                      <SC.Label>Referral ID: </SC.Label>
                      {id?.slice(0, 8)}...
                    </SC.Info>
                    <SC.Info>
                      <SC.Label>Button ID: </SC.Label>
                      {buttonId?.slice(0, 8)}
                    </SC.Info>
                  </SC.InfoSection>
                </Stack>
                <Stack
                  direction={isQuestion ? "column" : "row"}
                  flexWrap={"wrap"}
                  gap={isQuestion ? 1 : 4}
                >
                  <Stack flex={1} minWidth={"240px"} gap={1}>
                    <SC.Title>
                      Provider{providers?.length > 1 ? "s" : ""}
                    </SC.Title>
                    <SC.InfoSection>
                      <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignContent={"flex-start"}
                        flexWrap={"wrap"}
                        key={id}
                        gap={4}
                      >
                        {providers?.map(({ id, name, speciality }) => (
                          <Stack
                            key={id}
                            flex={1}
                            direction={"row"}
                            flexWrap={"wrap"}
                            gap={"10px"}
                          >
                            <SC.Info>
                              <SC.Label>Name:</SC.Label> {name}
                            </SC.Info>
                            <SC.Info>
                              <SC.Label>Speciality:</SC.Label> {speciality}
                            </SC.Info>
                          </Stack>
                        ))}
                      </Stack>
                    </SC.InfoSection>
                  </Stack>
                  <Stack minWidth={"240px"} flex={1} gap={1}>
                    <SC.Title>Recipient</SC.Title>
                    <SC.InfoSection>
                      <SC.Info>
                        <SC.Label>Name:</SC.Label> {recipientName}
                      </SC.Info>
                      <SC.Info>
                        <SC.Label>Phone:</SC.Label>{" "}
                        <a href={`tel:${recipientPhone}`}>{recipientPhone}</a>
                      </SC.Info>
                      <SC.Info>
                        <SC.Label>Address:</SC.Label> {recipientAddress}
                      </SC.Info>
                    </SC.InfoSection>
                  </Stack>
                </Stack>
                {!!patient && (
                  <Stack direction={"row"} gap={4}>
                    <Stack width={"100%"} gap={1}>
                      <SC.Title>Patient</SC.Title>
                      <SC.InfoSection>
                        <SC.Info>
                          <SC.Label>Name:</SC.Label> {patient?.name}
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Date of Birth:</SC.Label>{" "}
                          {displayDobTime(patient?.dob)}
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Gender:</SC.Label> {patient?.gender}
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Phone:</SC.Label>{" "}
                          <a href={`tel:${patient?.phone}`}>{patient?.phone}</a>
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Location:</SC.Label> {patient?.location}
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Reason:</SC.Label> {patient?.reason}
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Urgency:</SC.Label> {patient?.urgency}
                        </SC.Info>
                      </SC.InfoSection>
                    </Stack>
                  </Stack>
                )}
                {!!question && (
                  <Stack direction={"row"} gap={4}>
                    <Stack width={"100%"} gap={1}>
                      <SC.Title>Question</SC.Title>
                      <SC.InfoSection>
                        <SC.Info>
                          <SC.Label>Question:</SC.Label> {question}
                        </SC.Info>
                      </SC.InfoSection>
                    </Stack>
                  </Stack>
                )}
                {error && <Alert severity="error">{error}</Alert>}
              </Stack>
              <Messages
                referral={data}
                admins={admins}
                marketers={marketers}
                schedulers={schedulers}
                originator={originator}
                showError={showError}
              />
            </Stack>
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <FileList
              files={files}
              referralId={id}
              originator={originator}
              showError={showError}
              refreshData={loadData}
            />
          </TabPanel>
          {loading && <Spinner />}
        </Stack>
      }
      handleClose={handleClose}
      actions={
        <Stack
          flex={1}
          direction={"row"}
          gap={1}
          justifyContent={"center"}
          alignItems={"center"}
        ></Stack>
      }
      withCloseButton
    />
  );
};
